// LA PERILLA, SIN PANTALLA.
//
// Toda la lógica del aparato vive aquí, separada del dibujo, por dos
// razones prácticas:
//
//   1. Se puede probar sin navegador. El renderizador de servidor no
//      ejecuta efectos ni deja pulsar nada, así que si «girar» viviera
//      dentro del componente, la única forma de probarlo sería levantar
//      un navegador entero para comprobar que 3 + 1 son 4.
//   2. Es lo que el firmware de la UNO Q tendrá que hacer igual. Este
//      archivo ES la especificación de esa parte, escrita en algo que se
//      ejecuta: si mañana el aparato físico se comporta distinto, aquí
//      está la cuenta contra la que compararlo.
//
// EL APARATO TIENE UN SOLO CONTROL: girar, apretar, y mantener apretado.
// Todo lo demás son estados.

// Los estados de la máquina, y lo que significa cada uno para quien mira
// la pantalla. El nombre es lo que se dice, no un código.
export const ESTADOS = {
  SIN_ENROLAR: 'Pide los seis dígitos',
  VITRINA: 'Enseña la repisa y espera a que alguien escanee',
  MIRANDO: 'Alguien está vinculado y gira entre productos',
  EDITANDO: 'Está cambiando la cantidad de un producto',
};

/** Gira la lista de productos. Da la vuelta por los dos extremos: en un
 *  aparato de una sola perilla, llegar al final y no poder seguir es la
 *  forma más rápida de que alguien crea que se trabó. */
export function girarLista(indice, pasos, total) {
  if (total <= 0) return 0;
  return ((indice + pasos) % total + total) % total;
}

/** Gira la cantidad mientras se edita.
 *
 *  NO DEJA BAJAR DE CERO, y es deliberado: una existencia negativa no
 *  significa nada en una repisa, y el aparato tiene que impedirla ANTES
 *  de mandarla, no recibir el rechazo del servidor tres segundos después
 *  cuando quien giró ya se fue. */
export function girarCantidad(cantidad, pasos) {
  return Math.max(0, cantidad + pasos);
}

/** El delta que se manda: lo contado menos lo que había.
 *
 *  DELTA Y NO VALOR ABSOLUTO, que es la regla del §3.3 de la directriz:
 *  es lo que permite que dos ajustes encolados se apliquen en orden
 *  aunque la existencia haya cambiado por otro lado mientras el aparato
 *  estaba sin red. */
export function deltaDe(contado, habia) {
  return Number(contado) - Number(habia ?? 0);
}

/** Un giro confirmado, listo para la cola.
 *
 *  EL UUID SE GENERA EN EL APARATO Y NO EN EL SERVIDOR. Es lo que hace
 *  que reenviar el mismo lote no duplique nada: si lo pusiera el
 *  servidor, cada reintento sería un movimiento nuevo. */
export function giroConfirmado(sku, contado, habia, uuid, codigoLote = null) {
  const delta = deltaDe(contado, habia);
  if (delta === 0) return null;
  const giro = { id_operacion: uuid, sku, delta };
  // TC-30. OPCIONAL Y AL FINAL: un giro de un producto que no maneja
  // lotes sale exactamente igual que ayer, sin la clave siquiera. Es la
  // misma regla del contrato que sigue el servidor.
  if (codigoLote) giro.codigo_lote = codigoLote;
  return giro;
}

/** Cómo se lee un lote en una pantalla de dos líneas · TC-30.
 *
 *  EL PRIMERO DICE POR QUÉ ES EL PRIMERO. «Sugerido» a secas obliga a
 *  confiar; «sugerido · caduca antes» explica el FIFO sin un manual, que
 *  es la regla de que cada pantalla se explique sola.
 *
 *  Y SI EL LOTE ESTÁ RETENIDO POR CALIDAD, se dice aquí y no se esconde:
 *  la terminal no juzga —quien decide es calidad— pero elegir a ciegas
 *  el lote que está en cuarentena es cómo se manda material retenido a
 *  la línea. */
export function etiquetaDeLote(lote, esSugerido = false) {
  if (!lote) return '';
  const partes = [lote.codigo_lote];
  if (lote.existencia !== null && lote.existencia !== undefined) {
    partes.push(`quedan ${lote.existencia}`);
  }
  if (esSugerido) {
    partes.push(lote.fecha_caducidad ? 'sugerido · caduca antes' : 'sugerido');
  }
  if (lote.estado_calidad && lote.estado_calidad !== 'LIBERADO') {
    partes.push(`RETENIDO POR CALIDAD (${lote.estado_calidad.toLowerCase()})`);
  }
  return partes.join(' · ');
}

/** Lo que se pinta debajo del nombre del producto.
 *
 *  DURANTE UN CONTEO A CIEGAS NO SE DICE LA EXISTENCIA, y no es un
 *  adorno: si el aparato enseña «deberían ser 40», contar deja de ser
 *  contar y pasa a ser confirmar. */
export function leyendaDe(producto, aCiegas) {
  if (!producto) return '';
  if (aCiegas || producto.existencia === null || producto.existencia === undefined) {
    return '¿cuántas hay? · cuenta y captura';
  }
  return `quedan ${producto.existencia} ${(producto.unidad || '').toLowerCase()}`.trim();
}

/** Cómo se lee otra terminal en el menú del supervisor · TC-31.
 *
 *  NUNCA UN ÍCONO VERDE QUE MIENTE, y aquí hay TRES estados y no dos:
 *  una terminal que nunca ha latido no es «hace 0 minutos» ni «hace
 *  mucho» — es que todavía no ha dicho nada, y decirlo así es lo que
 *  permite distinguir «se cayó» de «nunca se encendió».
 *
 *  Y LOS PENDIENTES SON UN DATO SUYO, NO DEL SERVIDOR: son los que
 *  declaró en su último latido. Si lleva media hora sin hablar, ese
 *  número es de hace media hora, así que no se enseña solo. */
export function comoEstaLaTerminal(t) {
  if (!t) return { color: 'gris', frase: '' };
  if (t.nunca_ha_latido) {
    return { color: 'gris', frase: 'nunca ha dado señales' };
  }
  const m = Number(t.minutos_sin_latir ?? 0);
  const cola = t.pendientes > 0
    ? ` · ${t.pendientes} sin subir cuando habló`
    : '';
  if (m >= 60) return { color: 'rojo', frase: `sin hablar hace ${m} min${cola}` };
  if (m >= 5) return { color: 'ambar', frase: `habló hace ${m} min${cola}` };
  if (t.pendientes > 0) {
    return { color: 'ambar', frase: `al día · ${t.pendientes} sin subir` };
  }
  return { color: 'verde', frase: 'al día' };
}

/** El color del LED de estado, con la regla de la casa.
 *
 *  NUNCA UN ÍCONO VERDE QUE MIENTE (§A5): verde solo si la terminal
 *  habló con TEGO hace menos de un minuto Y no lleva nada sin subir. Un
 *  aparato que responde pero acumula veinte giros no está bien, y
 *  pintarlo de verde sería la mentira más cara de todas.
 */
export function ledDe({ pendientes = 0, ultimoContacto = null, vinculado = false,
                        ahora = Date.now() }) {
  if (ultimoContacto === null) return 'rojo';
  const segundos = (ahora - ultimoContacto) / 1000;
  if (segundos > 300) return 'rojo';
  if (pendientes > 0) return 'ambar';
  if (!vinculado) return 'azul';
  return 'verde';
}

export const LEYENDA_LED = {
  verde: 'Todo al día',
  ambar: 'Hay giros sin subir',
  rojo: 'Sin hablar con TEGO',
  azul: 'Esperando a que alguien escanee',
};

/** Los seis dígitos, con la perilla.
 *
 *  Girar elige el dígito (0–9, y da la vuelta), apretar avanza. Seis
 *  apretones y ya. Sin contraseñas tecleadas con una perilla, que es lo
 *  que el §3.1 de la directriz decidió. */
export function girarDigito(valor, pasos) {
  return ((valor + pasos) % 10 + 10) % 10;
}
